import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from 'components/Modal';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import { expandSection } from 'features/shared/collapseStorage';
import Segment from 'helpers/segment';
import { selectActiveVenue } from 'selectors/general';

import { getSkuCollapseKey, ITEM_AVAILABILITY_STATUS, STATUS_TO_BOOLEANS } from './SkuItems.constants';
import { buildSkuItemAvailabilityUpdatePayload } from './SkuItems.helpers';
import { getItemCategoryIds, getItemId, getItemName } from './SkuMenuData.normalizers';
import { SKU_ADD_ITEM_FLYOUT_ID } from './components/SkuAddItemForm';
import { SKU_EDIT_ITEM_FLYOUT_ID } from './components/SkuEditItemForm';
import { SKU_REMOVE_ITEM_FROM_CATEGORY_MODAL_NAME } from './components/SkuRemoveItemFromCategoryModal';
import useSkuMutations from './useSkuMutations';
import { getSkuVenueSource } from './SkuVenueSource.helpers';
import { SKU_ANALYTICS_EVENTS } from './SkuAnalytics.constants';
import messages from './SkuMenuPage.messages';

const useSkuItemActions = ({ venueId, refetch, openFlyout }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const venue = useSelector(selectActiveVenue);
  const source = getSkuVenueSource(venue);
  const { statusChange, removeFromCategory, removeFromAllCategories } = useSkuMutations({ venueId });

  const [preselectedCategoryIds, setPreselectedCategoryIds] = useState([]);
  const [selectedItemForEdit, setSelectedItemForEdit] = useState(null);
  const [preselectedEditCategoryIds, setPreselectedEditCategoryIds] = useState([]);

  const handleAddItemClick = category => {
    setPreselectedCategoryIds(category?.id ? [category.id] : []);
    openFlyout?.(SKU_ADD_ITEM_FLYOUT_ID);
  };

  const handleAddItemSuccess = () => {
    const storageKey = getSkuCollapseKey(venueId);
    preselectedCategoryIds.forEach(catId => expandSection(storageKey, `cat-${catId}`));
    refetch();
  };

  const handleEditItemClick = (item, category) => {
    const selectedItem = item?.raw || item || null;
    setSelectedItemForEdit(selectedItem);
    const itemCategoryIds = getItemCategoryIds(item?.raw || item);
    const fallbackCategoryIds = category?.id ? [category.id] : [];
    setPreselectedEditCategoryIds(itemCategoryIds.length ? itemCategoryIds : fallbackCategoryIds);
    openFlyout?.(SKU_EDIT_ITEM_FLYOUT_ID);
  };

  const handleEditItemSuccess = () => {
    // Refetch needed: edit form syncs category relationships server-side, not captured by optimistic update
    refetch();
  };

  const handleRemoveFromSingleCategory = useCallback(
    async (normalizedItem, categoryId, itemName) => {
      const itemId = getItemId(normalizedItem);
      await removeFromCategory.mutateAsync(
        { itemId, categoryId },
        {
          onSuccess: () => {
            Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_ITEM_REMOVED, { venueId, categoryId, itemId, source });
            dispatch(
              createSuccessSnackbar(
                messages.removeItemSuccess,
                { itemName, strong: chunks => <strong>{chunks}</strong> },
                { isLowStyle: true }
              )
            );
          },
          onError: () => {
            dispatch(
              createErrorSnackbar(
                messages.removeItemError,
                { strong: chunks => <strong>{chunks}</strong> },
                { isLowStyle: true }
              )
            );
          },
        }
      );
    },
    [dispatch, removeFromCategory]
  );

  const handleRemoveFromAllCategories = useCallback(
    async (normalizedItem, itemName) => {
      const itemId = getItemId(normalizedItem);
      const categoryIds = getItemCategoryIds(normalizedItem);
      await removeFromAllCategories.mutateAsync(
        { itemId, categoryIds },
        {
          onSuccess: () => {
            categoryIds.forEach(categoryId => {
              Segment.event(SKU_ANALYTICS_EVENTS.CATEGORY_ITEM_REMOVED, { venueId, categoryId, itemId, source });
            });
            dispatch(
              createSuccessSnackbar(
                messages.removeFromAllMenusSuccess,
                { itemName, strong: chunks => <strong>{chunks}</strong> },
                { isLowStyle: true }
              )
            );
          },
          onError: () => {
            dispatch(
              createErrorSnackbar(
                messages.removeItemError,
                { strong: chunks => <strong>{chunks}</strong> },
                { isLowStyle: true }
              )
            );
          },
        }
      );
    },
    [dispatch, removeFromAllCategories]
  );

  const handleStatusChange = useCallback(
    (item, nextStatus) => {
      const normalizedItem = item?.raw || item || null;
      const itemId = getItemId(normalizedItem);
      if (!itemId) return;

      const statusBooleans = STATUS_TO_BOOLEANS[nextStatus];
      if (!statusBooleans) return;
      const { enabled, soldOut, partnerState } = statusBooleans;
      const payload = buildSkuItemAvailabilityUpdatePayload(normalizedItem, statusBooleans);

      const previousStatus =
        Object.entries(STATUS_TO_BOOLEANS).find(
          ([, vals]) =>
            vals.enabled === (normalizedItem?.enabled ?? true) && vals.soldOut === (normalizedItem?.soldOut ?? false)
        )?.[0] || ITEM_AVAILABILITY_STATUS.AVAILABLE;

      statusChange.mutate(
        {
          itemId,
          optimistic: { enabled, soldOut, partnerState },
          payload,
        },
        {
          onSuccess: () => {
            Segment.event(SKU_ANALYTICS_EVENTS.ITEM_AVAILABILITY_CHANGED, {
              venueId,
              itemId,
              from: previousStatus,
              to: nextStatus,
              source,
            });
            dispatch(
              createSuccessSnackbar(
                messages.itemStatusChangeSuccess,
                {
                  itemName: getItemName(normalizedItem),
                  strong: chunks => <strong>{chunks}</strong>,
                },
                { isLowStyle: true }
              )
            );
          },
          onError: () => {
            dispatch(
              createErrorSnackbar(
                messages.itemStatusChangeError,
                { strong: chunks => <strong>{chunks}</strong> },
                { isLowStyle: true }
              )
            );
          },
        }
      );
    },
    [dispatch, statusChange]
  );

  const handleDeleteItem = useCallback(
    (item, category) => {
      const normalizedItem = item?.raw || item || null;
      const itemId = getItemId(normalizedItem);
      const itemName = getItemName(normalizedItem) || intl.formatMessage(messages.itemLabel);
      const categoryId = category?.id;
      if (!itemId || !categoryId) {
        return;
      }

      const categoryCount = getItemCategoryIds(normalizedItem).length;

      dispatch(
        openModal(SKU_REMOVE_ITEM_FROM_CATEGORY_MODAL_NAME, {
          itemName,
          categoryName: category.name,
          categoryCount,
          onRemoveFromCategory: () => handleRemoveFromSingleCategory(normalizedItem, categoryId, itemName),
          onRemoveFromAllMenus: () => handleRemoveFromAllCategories(normalizedItem, itemName),
        })
      );
    },
    [dispatch, handleRemoveFromSingleCategory, handleRemoveFromAllCategories, intl]
  );

  return {
    preselectedCategoryIds,
    selectedItemForEdit,
    preselectedEditCategoryIds,
    handleAddItemClick,
    handleAddItemSuccess,
    handleEditItemClick,
    handleEditItemSuccess,
    handleStatusChange,
    handleDeleteItem,
  };
};

export default useSkuItemActions;
